import { type ReactNode, useEffect } from "react";
import { ArrowLeft, type LucideIcon } from "lucide-react";
import { Button as TamaguiButton, XStack, YStack } from "tamagui";
import { useNavigate } from "react-router-dom";

import { KindredLogoButton, MemberHeaderActions } from "@/components/MemberHeaderControls";

import {
  AppCard,
  AppCardBody,
  AppContentContainer,
  AppHeroShell,
  AppPage,
  AppShell,
  AppShellMain,
  AppText,
  kindredFontFamilies,
} from "../../ui-foundation";

type AdminPageScaffoldProps = {
  title: string;
  subtitle?: string;
  action?: ReactNode;
  fallbackPath?: string;
  titleLines?: number;
  children: ReactNode;
};

type AdminIconButtonProps = {
  icon: LucideIcon;
  label: string;
  onPress: () => void;
};

type AdminStatusTone = "neutral" | "primary" | "secondary" | "success" | "warning" | "danger";

type AdminStatusPillProps = {
  tone?: AdminStatusTone;
  children: ReactNode;
};

type AdminMetricCardProps = {
  label: string;
  value: number | string;
  icon?: LucideIcon;
  detail?: string;
  onPress?: () => void;
};

type AdminInitialAvatarProps = {
  name?: string | null;
  size?: number;
};

const STATUS_TONES: Record<AdminStatusTone, { backgroundColor: string; borderColor: string; color: string }> = {
  neutral: {
    backgroundColor: "$background",
    borderColor: "$borderColor",
    color: "$colorMuted",
  },
  primary: {
    backgroundColor: "$primarySoft",
    borderColor: "$primarySoft",
    color: "$primary",
  },
  secondary: {
    backgroundColor: "$secondarySoft",
    borderColor: "$secondarySoft",
    color: "$secondary",
  },
  success: {
    backgroundColor: "$accentSoft",
    borderColor: "$success",
    color: "$success",
  },
  warning: {
    backgroundColor: "$accentSoft",
    borderColor: "$accent",
    color: "$accent",
  },
  danger: {
    backgroundColor: "$primarySoft",
    borderColor: "$destructive",
    color: "$destructive",
  },
};

export function AdminPageScaffold({
  title,
  subtitle,
  action,
  fallbackPath = "/my-kindred",
  titleLines = 1,
  children,
}: AdminPageScaffoldProps) {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate(fallbackPath);
  };

  return (
    <AppShell minHeight="100vh">
      <header className="relative z-20">
        <AppContentContainer pt="$xs">
          <YStack gap="$xs">
            <AppHeroShell
              title={title}
              titleLines={titleLines}
              leading={<KindredLogoButton />}
              backAction={
                <TamaguiButton
                  unstyled
                  type="button"
                  width={40}
                  height={40}
                  alignItems="center"
                  justifyContent="center"
                  borderWidth={1}
                  borderRadius="$pill"
                  borderColor="$borderColor"
                  backgroundColor="$cardBackground"
                  pressStyle={{ opacity: 0.92 }}
                  onPress={handleBack}
                  aria-label="Go back"
                >
                  <ArrowLeft size={18} className="text-secondary" />
                </TamaguiButton>
              }
              action={<MemberHeaderActions>{action}</MemberHeaderActions>}
            />
            {subtitle ? (
              <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">
                {subtitle}
              </AppText>
            ) : null}
          </YStack>
        </AppContentContainer>
      </header>

      <AppShellMain>
        <AppPage tag="main" gap="$md" maxWidth={1120}>
          {children}
        </AppPage>
      </AppShellMain>
    </AppShell>
  );
}

export function AdminIconButton({ icon: Icon, label, onPress }: AdminIconButtonProps) {
  return (
    <TamaguiButton
      unstyled
      type="button"
      width={40}
      height={40}
      alignItems="center"
      justifyContent="center"
      borderWidth={1}
      borderRadius="$pill"
      borderColor="$borderColor"
      backgroundColor="$cardBackground"
      pressStyle={{ opacity: 0.92 }}
      onPress={onPress}
      aria-label={label}
    >
      <Icon size={18} className="text-secondary" />
    </TamaguiButton>
  );
}

export function AdminStatusPill({ tone = "neutral", children }: AdminStatusPillProps) {
  const palette = STATUS_TONES[tone];

  return (
    <XStack
      alignItems="center"
      justifyContent="center"
      borderWidth={1}
      borderRadius="$pill"
      px="$sm"
      py={5}
      backgroundColor={palette.backgroundColor}
      borderColor={palette.borderColor}
    >
      <AppText
        color={palette.color}
        fontFamily={kindredFontFamilies.sansBold}
        fontSize="$xs"
        lineHeight="$xs"
      >
        {children}
      </AppText>
    </XStack>
  );
}

export function AdminMetricCard({ label, value, icon: Icon, detail, onPress }: AdminMetricCardProps) {
  const content = (
    <AppCard flexBasis={0} flexGrow={1} minWidth={160}>
      <AppCardBody alignItems="center" gap="$xs">
        {Icon ? <Icon size={22} className="text-primary" /> : null}
        <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$hero" lineHeight="$hero" color="$heading">
          {value}
        </AppText>
        <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$sm" lineHeight="$sm" textAlign="center">
          {label}
        </AppText>
        {detail ? (
          <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm" textAlign="center">
            {detail}
          </AppText>
        ) : null}
      </AppCardBody>
    </AppCard>
  );

  if (!onPress) {
    return content;
  }

  return (
    <button type="button" onClick={onPress} className="flex min-w-[160px] flex-1 text-left">
      {content}
    </button>
  );
}

export function AdminInitialAvatar({ name, size = 44 }: AdminInitialAvatarProps) {
  const initial = (name || "?").trim().charAt(0).toUpperCase() || "?";

  return (
    <YStack
      width={size}
      height={size}
      borderRadius="$pill"
      alignItems="center"
      justifyContent="center"
      backgroundColor="$primarySoft"
      flexShrink={0}
    >
      <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$md" lineHeight="$md" color="$primary">
        {initial}
      </AppText>
    </YStack>
  );
}
